import { createServerFn } from '@tanstack/react-start'
import { getRequestIP } from '@tanstack/react-start/server'
import { z } from 'zod'

import { upsertLead } from '@/db/repositories/leads'
import TrialRequestEmail, { type PlanDisplay } from '@/emails/trial-request'
import {
  PLAN_CONFIG,
  type CompliassistPlanId,
} from '@/features/compliassist-pricing/data/plans'
import { getTranslations } from '@/i18n/translator'
import { buildTrackedEmailLink } from '@/lib/analytics/email-redirect-url'
import { sendReactEmail } from '@/lib/aws/render-email'
import {
  BOOKING_URL,
  BUILDING_TIER_CONTENT,
  resolveBuildingTier,
} from '@/lib/building-tier'
import { emailField } from '@/lib/email/schema'
import { isHoneypotFilled } from '@/lib/honeypot'
import { inboundRouting } from '@/lib/hubspot/routing'
import { buildDealName, syncLeadToHubspot } from '@/lib/hubspot/sync-lead'
import {
  formatEstablishmentNames,
  groupEstablishmentsBySiteType,
} from '@/lib/naf/group-establishments'
import { resolveErpSubType } from '@/lib/naf/resolve-erp-subtype'
import { resolveSiteType } from '@/lib/naf/resolve-site-type'
import { captureError, warn } from '@/lib/observability'
import { buildVideoPreviewUrl } from '@/lib/og/video-preview-url'
import { isRateLimited } from '@/lib/rate-limit'
import { fetchRegulatoryNews } from '@/lib/sanity/regulatory-news'
import { SITE_URL } from '@/lib/seo'
import { notifyNewLead } from '@/lib/teams'
import { resolveTrialContent } from '@/lib/trial-categories'

const REPLACE_MARKER = 'REPLACE_'

function hasPlaceholderVideo(content: {
  videoThumbnailUrl: string
  videoUrl: string
}): boolean {
  return [content.videoThumbnailUrl, content.videoUrl].some((url) =>
    url.includes(REPLACE_MARKER),
  )
}

const establishmentSchema = z.object({
  siret: z.string(),
  name: z.string(),
  apeCode: z.string(),
  isUai: z.boolean(),
  isFiness: z.boolean(),
})

const companySchema = z.object({
  siren: z.string(),
  siret: z.string(),
  name: z
    .string()
    .trim()
    .min(1)
    .transform((s) => s.slice(0, 120)),
  apeCode: z.string(),
  address: z.string(),
  establishmentsCount: z.number().int().nonnegative(),
  openEstablishmentsCount: z.number().int().nonnegative(),
  isUai: z.boolean(),
  isFiness: z.boolean(),
  establishments: z.array(establishmentSchema),
})

const trialRequestSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1)
    .transform((s) => s.slice(0, 120)),
  lastName: z
    .string()
    .trim()
    .min(1)
    .transform((s) => s.slice(0, 120)),
  email: emailField,
  company: companySchema,
  phone: z.string().trim().optional(),
  locale: z.enum(['fr', 'en']).optional(),
  website: z.string().optional().default(''),
})

export type TrialRequestInput = z.infer<typeof trialRequestSchema>

export type SubmitTrialRequestResponse =
  { ok: true } | { ok: false; error: 'validation' | 'server' }

function planDisplay(
  planId: CompliassistPlanId,
  tPricing: Awaited<ReturnType<typeof getTranslations>>,
  withFeatures: boolean,
): PlanDisplay {
  return {
    title: tPricing(`pricing.plans.${planId}.title`),
    description: tPricing(`pricing.plans.${planId}.description`),
    features: withFeatures
      ? PLAN_CONFIG[planId].unlocked.map((key) =>
          tPricing(`pricing.featureList.${key}`),
        )
      : [],
  }
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function buildTrialNote(input: {
  company: {
    name: string
    siren: string
    siret: string
    apeCode: string
    establishmentsCount: number
  }
  siteType: string
}): string {
  const c = input.company
  return [
    `<p><strong>Essai gratuit CompliAssist</strong></p>`,
    `<ul>`,
    `<li>Société : ${escapeHtml(c.name)}</li>`,
    `<li>SIREN : ${escapeHtml(c.siren)}</li>`,
    `<li>SIRET : ${escapeHtml(c.siret)}</li>`,
    `<li>Code APE : ${escapeHtml(c.apeCode)}</li>`,
    `<li>Type de site : ${escapeHtml(input.siteType)}</li>`,
    `<li>Établissements : ${c.establishmentsCount}</li>`,
    `</ul>`,
  ].join('')
}

async function submitTrialRequestImpl(
  input: TrialRequestInput,
): Promise<SubmitTrialRequestResponse> {
  const parsed = trialRequestSchema.safeParse(input)
  if (!parsed.success) {
    return { ok: false, error: 'validation' }
  }

  const {
    firstName,
    lastName,
    email,
    company,
    phone,
    locale: inputLocale,
    website,
  } = parsed.data
  const locale = inputLocale ?? 'fr'

  if (isHoneypotFilled(website)) {
    warn('trial-request', 'honeypot triggered, silently accepting')
    return { ok: true }
  }

  const siteType = await resolveSiteType({
    apeCode: company.apeCode,
    siret: company.siret,
    isUai: company.isUai,
    isFiness: company.isFiness,
  })

  // Siège-based sector for the HubSpot company property, refined to an ERP
  // sub-type (hotel/restaurant/…) so it matches the secteur_dactivite_inbound
  // dropdown options 1:1.
  const sectorInbound =
    siteType === 'erp'
      ? (resolveErpSubType(company.apeCode) ?? 'erp')
      : siteType

  let leadId: string
  try {
    const lead = await upsertLead({
      email,
      phone,
      company: company.name,
      siren: company.siren,
      siret: company.siret,
      apeCode: company.apeCode,
      siteType,
      address: company.address,
      establishmentsCount: company.establishmentsCount,
    })
    leadId = lead.id
  } catch (error) {
    console.error('[submitTrialRequest] DB persistence failed', error)
    return { ok: false, error: 'server' }
  }

  try {
    const t = await getTranslations({ locale, namespace: 'cta' })
    const tPricing = await getTranslations({
      locale,
      namespace: 'compliassist',
    })

    // A company's establishments can span several sectors (e.g. a head
    // office plus a factory) — fall back to the siège alone if the search
    // API returned no establishment list at all.
    const establishmentsForGrouping =
      company.establishments.length > 0
        ? company.establishments
        : [
            {
              siret: company.siret,
              name: company.name,
              apeCode: company.apeCode,
              isUai: company.isUai,
              isFiness: company.isFiness,
            },
          ]

    const rawGroups = await groupEstablishmentsBySiteType(
      establishmentsForGrouping,
    )
    const [primaryGroup] = rawGroups
    const primaryContent = resolveTrialContent(primaryGroup)
    const showVideo = !hasPlaceholderVideo(primaryContent)

    const siteTypeGroups = rawGroups.map((group) => {
      const content = resolveTrialContent(group)
      return {
        sectorLabel: content.sectorLabel[locale],
        establishmentsLabel: formatEstablishmentNames(
          group.establishments.map((e) => e.name),
          locale,
        ),
        countLabel: t('trialForm.email.establishmentCount', {
          count: group.establishments.length,
        }),
        controls: content.typicalControls[locale],
      }
    })

    const buildingTier = resolveBuildingTier(company.openEstablishmentsCount)
    const tierContent = BUILDING_TIER_CONTENT[buildingTier]

    const allPlanIds: CompliassistPlanId[] = ['simple', 'pilot', 'master']
    const recommendedPlan = tierContent.compliassistPlanId
      ? planDisplay(tierContent.compliassistPlanId, tPricing, true)
      : {
          title: tPricing('pricing.enterprise.title'),
          description: tPricing('pricing.enterprise.description'),
          features: [],
        }
    const otherPlans = allPlanIds
      .filter((id) => id !== tierContent.compliassistPlanId)
      .map((id) => planDisplay(id, tPricing, false))

    const newsMatch = await fetchRegulatoryNews(
      primaryContent.regulatoryNewsKeywords,
      locale,
    )
    const primaryRegulatoryNews = newsMatch
      ? {
          fallbackText: '',
          title: newsMatch.title,
          excerpt: newsMatch.excerpt,
          url: newsMatch.url,
          imageUrl: newsMatch.imageUrl,
        }
      : {
          fallbackText: t('trialForm.email.regulatoryNewsFallback'),
          title: null,
          excerpt: null,
          url: null,
          imageUrl: null,
        }

    const strings = {
      preview: t('trialForm.email.preview'),
      greeting: locale === 'en' ? 'Hello,' : 'Bonjour,',
      intro: t('trialForm.email.intro', { company: company.name }),
      establishmentsHeading: t('trialForm.email.establishmentsHeading'),
      controlsLabel: t('trialForm.email.controlsLabel'),
      notExhaustiveNote: t('trialForm.email.notExhaustiveNote'),
      regulatoryHeading: t('trialForm.email.regulatoryHeading'),
      regulatoryIntro: t('trialForm.email.regulatoryIntro'),
      regulatoryNoteFor: t('trialForm.email.regulatoryNoteFor'),
      regulatoryOtherSectors: t('trialForm.email.regulatoryOtherSectors'),
      howItWorksHeading: t('trialForm.email.howItWorksHeading'),
      howItWorksNote: t('trialForm.email.howItWorksNote'),
      planHeading: t('trialForm.email.planHeading'),
      planIntro: t('trialForm.email.planIntro'),
      otherOptionsIntro: t('trialForm.email.otherOptionsIntro'),
      comparePlansLabel: t('trialForm.email.comparePlansLabel'),
      signoff: t('trialForm.email.signoff'),
    }

    await sendReactEmail(
      TrialRequestEmail({
        companyName: company.name,
        siteTypeGroups,
        primaryRegulatoryNews,
        hasMultipleSectors: rawGroups.length > 1,
        showVideo,
        videoThumbnailUrl: buildVideoPreviewUrl(
          primaryContent.videoThumbnailUrl,
        ),
        videoUrl: buildTrackedEmailLink({
          to: primaryContent.videoUrl,
          event: 'email_video_click',
          bookingId: leadId,
        }),
        recommendedPlan,
        otherPlans,
        // CompliAssist checkout links aren't wired up yet — every tier routes
        // to booking a call instead, regardless of the recommended plan.
        ctaUrl: buildTrackedEmailLink({
          to: BOOKING_URL,
          event: 'email_cta_click',
          bookingId: leadId,
        }),
        ctaLabel: t('trialForm.email.fallbackCtaLabel'),
        comparePlansUrl: `${SITE_URL}/${locale}/pricing`,
        strings,
      }),
      {
        to: email,
        subject:
          locale === 'en'
            ? `${company.name}, here's your personalized compliance overview`
            : `${company.name}, voici votre suivi de conformité personnalisé`,
      },
    )
  } catch (error) {
    captureError('trialRequest.emailSend', error, { to: email })
  }

  let dealId: string | null = null
  try {
    const routing = {
      ...inboundRouting('emailSent'),
      priority: 'medium' as const,
    }
    const sync = await syncLeadToHubspot({
      leadId,
      toolSlug: 'compliassist',
      contact: {
        email,
        firstName,
        lastName,
        phone: phone ?? null,
        company: company.name,
      },
      routing,
      dealName: buildDealName(company.name, 'compliassist'),
      noteBodyHtml: buildTrialNote({ company, siteType }),
      companyProperties: { secteur_dactivite_inbound: sectorInbound },
    })
    dealId = sync.dealId
  } catch (error) {
    captureError('trialRequest.hubspotSync', error, { leadId, email })
  }

  await notifyNewLead({
    heading: '🚀 Nouvel essai gratuit',
    contact: { email, phone, company: company.name },
    extra: [
      { title: 'SIREN', value: company.siren },
      { title: 'Type de site', value: siteType },
      ...(dealId ? [{ title: 'DealId', value: dealId }] : []),
    ],
  })

  return { ok: true }
}

export const submitTrialRequest = createServerFn({ method: 'POST' })
  .validator((data: unknown) => data as TrialRequestInput)
  .handler(async ({ data }) => {
    const ip = getRequestIP({ xForwardedFor: true }) ?? 'unknown'
    if (
      isRateLimited(`trial-request:${ip}`, { max: 5, windowMs: 10 * 60_000 })
    ) {
      return { ok: false, error: 'server' } as const
    }
    return submitTrialRequestImpl(data)
  })
